import { useState } from "react";
import { Button, Form, Input, message } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { ShieldCheck } from "lucide-react";
import { authApi } from "../../../api/auth";
import { useAuthStore } from "../../../store/useAuthStore";

export default function Login() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { setAuth } = useAuthStore();
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const res = await authApi.login(values.email, values.password);
      const data = res.data.data as any;
      if (data.requires_2fa === true) {
        setTempToken(data.temp_token);
        setStep("otp");
        return;
      }
      const { token, admin } = data;
      setAuth(admin, token);
      window.location.reload();
    } catch (err: any) {
      messageApi.error(err?.message || "Login failed. Check credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      messageApi.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.verifyTwoFactor(tempToken, otpCode);
      const { token, admin } = (res as any).data.data;
      setAuth(admin, token);
      window.location.reload();
    } catch (err: any) {
      messageApi.error(err?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const leftPanel = (
    <div className="hidden md:flex w-1/2 relative items-center justify-center overflow-hidden bg-violet-950">
      <div className="absolute inset-0 bg-gradient-to-br from-violet-950/95 via-violet-900/90 to-slate-950/90" />
      <div className="absolute top-[-120px] left-[-120px] w-80 h-80 bg-violet-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-100px] right-[-100px] w-72 h-72 bg-violet-400/10 rounded-full blur-3xl" />
      <div className="relative z-10 text-white text-center px-10">
        <div className="flex items-center justify-center mb-6">
          <div className="w-50 h-20">
            <img src="/sitepro_logo.png" alt="" width={400} height={400} className="w-full h-full" />
          </div>
        </div>
        <p className="text-slate-300 mt-4 text-sm leading-relaxed max-w-md">
          Manage all companies, plans, industries, RBAC and platform-wide settings.
        </p>
        <div className="mt-8 text-xs text-slate-400 tracking-wide">
          Create companies • Manage plans • Configure RBAC
        </div>
        <div className="mt-6 inline-flex items-center gap-2 bg-violet-800/50 border border-violet-700/50 rounded-full px-4 py-2 text-xs text-violet-300">
          <ShieldCheck size={12} />
          RESTRICTED — authorised personnel only
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {contextHolder}

      {leftPanel}

      {/* Right form panel */}
      <div className="relative flex w-full md:w-1/2 items-center justify-center bg-slate-100">
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-violet-50/90 to-slate-100/95" />

        <div className="relative z-10 w-full max-w-md px-6">
          <div className="bg-white/95 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-violet-200/60">
            <div className="flex justify-center mb-5">
              <div className="w-50 h-20">
            <img src="/dark_logo.png" alt="" width={400} height={400} className="w-full h-full" />
          </div>
            </div>

            {step === "credentials" ? (
              <>
                <p className="text-center text-sm text-slate-500 mt-1 mb-2">Onap SitePro Admin Access</p>
                <div className="flex justify-center mb-6">
                  <span className="text-[11px] bg-violet-100 text-violet-700 border border-violet-200 px-2.5 py-0.5 rounded-full font-semibold">
                    RESTRICTED — authorised personnel only
                  </span>
                </div>

                <Form form={form} layout="vertical" size="large" onFinish={handleLogin}>
                  <Form.Item
                    name="email"
                    rules={[{ required: true, type: "email", message: "Enter your admin email" }]}
                  >
                    <Input
                      placeholder="Platform Admin Email"
                      prefix={<MailOutlined className="text-slate-400" />}
                      className="rounded-xl h-12"
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "Password is required" }]}
                  >
                    <Input.Password
                      placeholder="Password"
                      prefix={<LockOutlined className="text-slate-400" />}
                      className="rounded-xl h-12"
                    />
                  </Form.Item>
                  <Button
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    style={{
                      background: "#4c1d95",
                      borderColor: "#4c1d95",
                      color: "#fff",
                      borderRadius: 12,
                      height: 48,
                      fontWeight: 500,
                    }}
                  >
                    Access Platform Admin
                  </Button>
                </Form>
              </>
            ) : (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">Two-Factor Authentication</h2>
                <p className="text-center text-sm text-slate-500 mt-1 mb-6">
                  Enter the 6-digit code from your authenticator app
                </p>

                <div className="flex justify-center mb-4">
                  <Input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    size="large"
                    className="rounded-xl h-14 text-center text-2xl tracking-[0.5em] font-mono"
                    style={{ textAlign: "center", letterSpacing: "0.4em", fontSize: 22 }}
                    onPressEnter={handleVerifyOtp}
                  />
                </div>

                <Button
                  onClick={handleVerifyOtp}
                  loading={loading}
                  block
                  size="large"
                  style={{
                    background: "#4c1d95",
                    borderColor: "#4c1d95",
                    color: "#fff",
                    borderRadius: 12,
                    height: 48,
                    fontWeight: 500,
                  }}
                >
                  Verify Code
                </Button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => { setStep("credentials"); setOtpCode(""); setTempToken(""); }}
                    className="text-xs text-violet-600 hover:text-violet-800 underline underline-offset-2"
                  >
                    ← Back to login
                  </button>
                </div>
              </>
            )}

            <div className="text-center text-xs text-slate-400 mt-6">
              Onap SitePro © {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
